
<template>
    <b-container
        v-if="client && isNotPaid"
        id="lx-online-booking-checkout-payment-request"
        class="lx-online-booking-checkout-payment-request pb-4"
    >
        <validation-observer ref="form" v-slot="{ meta }">
            <card
                class="lx-online-booking-checkout-payment-request__card__tech-res"
                :headerContentClass="['w-100']"
                v-for="(techReservation, index) in techReservations"
                :key="index"
            >
                <template #header-icon>
                    <font-awesome-icon :icon="['fas', 'hotel']" size="lg" />
                </template>
                <template #header-content>
                    <div class="lx-online-booking-checkout-payment-request__card__tech-res__name">
                        {{ techReservation.tech_reservation.property_name }}
                    </div>
                </template>
                <template #body>
                    <div class="lx-online-booking-checkout-payment-request__card__tech-res__property">
                        <span
                            v-if="techReservation.property.thumbnail"
                            class="lx-online-booking-checkout-payment-request__card__tech-res__property__image"
                        >
                            <img :src="techReservation.property.thumbnail" />
                        </span>
                        <online-booking-invoice
                            class="lx-online-booking-checkout-payment-request__card__tech-res__property__info"
                            :techReservation="techReservation"
                            :showPropertyName="false"
                        />
                    </div>
                </template>
            </card>
            <online-booking-checkout-client-details
                class="lx-online-booking-checkout-payment-request__card client-details"
                :showDateOfBirth="false"
                :showMiscellaneous="false"
            />
            <online-booking-checkout-payment
                class="lx-online-booking-checkout-payment-request__card"
                :fromDate="fromDate"
                :usePaymentsIntent="usePaymentsIntent"
                :paymentAmount="paymentAmount"
                :isChannexAllowed="true"
                :isPaymentRequest="true"
                :invalid="!meta.valid"
                @submitPayment="submitPayment"
            />
        </validation-observer>
        <modal
            :id="paymentErrorModalId"
            title="Payment Error"
            header-bg-variant="danger"
            header-text-variant="light"
            ok-only
        >
            <span v-html="errorModalMessage"></span>
        </modal>
        <modal
            :id="eCheckConfirmationModalId"
            title="Payment Method Confirmation"
            ok-title="Accept"
            :ok-disabled="acceptEcheckPaymentDisabled"
            @ok.prevent="acceptEcheckPaymentMethod"
        >
            <busy-modal :show="acceptEcheckPaymentDisabled"></busy-modal>
            <b-row>
                <b-col>
                    <b>Agreement Date:</b>
                </b-col>
                <b-col cols="auto">
                    <b>{{ eCheckAgreementDate }}</b>
                </b-col>
            </b-row>
            <b-row class="pt-2">
                <b-col>
                    <b>Full Name:</b>
                </b-col>
                <b-col cols="auto">
                    <b>{{ client.echeck_account_name }}</b>
                </b-col>
            </b-row>
            <b-row class="pt-4">
                <b-col>{{ eCheckMandateMessage }}</b-col>
            </b-row>
        </modal>
    </b-container>
    <b-container v-else-if="client">
        <b-alert show :variant="paymentRequestStatusVariant">
            <b>{{ paymentRequestStatusMessage }}</b>
        </b-alert>
    </b-container>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';
import cloneDeep from 'lodash/cloneDeep';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import format from 'date-fns/format';
import API from '../../../api.jsx';
import Card from '../../generic/card/card.vue';
import Modal from '../../generic/modal/modal.vue';
import BusyModal from '../../generic/busy-modal/busy-modal.vue';
import OnlineBookingInvoice from '../common/online-booking-invoice.vue';
import OnlineBookingCheckoutPayment from '../checkout/payment/online-booking-checkout-payment.vue';
import OnlineBookingCheckoutClientDetails from '../checkout/client-details/online-booking-checkout-client-details.vue';
import { formatResponseError } from '../../../utils/api';
import { toAmountDisplay } from '../../../utils/numbers';
import { maskNumber } from '../../../utils/strings';
import {
    ONLINE_BOOKING_PAYMENT_REQUEST_CLIENT_ENDPOINT,
    ONLINE_BOOKING_PAYMENT_REQUEST_PROCESS_PAYMENT_ENDPOINT,
    ONLINE_BOOKING_PAYMENT_REQUEST_TECH_RESERVATIONS_ENDPOINT,
    ONLINE_BOOKING_PAYMENT_REQUEST_VALIDATE_CLIENT_FIELDS_ENDPOINT,
} from '../../../endpoints.js';
import { PAYMENT_METHOD_CREDIT_CARD, PAYMENT_METHOD_E_CHECK } from '../../../constants.js';

export default {
    name: 'OnlineBookingReservation',
    components: {
        Card,
        Modal,
        BusyModal,
        OnlineBookingInvoice,
        OnlineBookingCheckoutPayment,
        OnlineBookingCheckoutClientDetails,
    },
    data() {
        return {
            paymentIntent: null,
            paymentIntentId: null,
            techReservations: [],
            errorModalMessage: '',
            paymentErrorModalId: 'payment-error-modal-id',
            eCheckAgreementDate: null,
            eCheckBankName: null,
            eCheckRoutingNumber: null,
            eCheckAccountNumber: null,
            eCheckMandateMessage: `You have authorized Lodgix to debit your bank account periodically
                    if and when you use Lodgix services or purchase more than one of Lodgix products 
                    periodically pursuant to Lodgix terms. Payments that fall outside of the regular debits 
                    authorized above will only be debited after your authorization is obtained.`,
            eCheckConfirmationModalId: 'echeck-confirmation-modal-id',
            acceptEcheckPaymentDisabled: false,
            showPendingConfirmationModal: false,
        };
    },
    computed: {
        ...mapGetters('onlineBooking', ['website', 'client', 'stripeClientSecret', 'countries', 'states']),
        ...mapGetters('stripe', ['stripe', 'card']),
        ...mapGetters('header', ['userUnicodeDateFormat']),
        isNotPaid() {
            return this.client.payment_status == 'not_paid';
        },
        paymentRequestStatusMessage() {
            if (this.client.payment_status == 'paid') {
                return 'The payment request has been paid.';
            } else if (this.client.payment_status == 'does_not_exist') {
                return 'The payment request does not exist.';
            } else if (this.client.payment_status == 'expired') {
                return 'The payment request has expired.';
            } else if (this.client.payment_status == 'subscription_expired') {
                return 'The subscription has expired.';
            }
        },
        paymentRequestStatusVariant() {
            if (this.client.payment_status == 'paid') {
                return 'success';
            } else if (this.client.payment_status == 'does_not_exist') {
                return 'danger';
            } else if (this.client.payment_status == 'expired') {
                return 'info';
            } else if (this.client.payment_status == 'subscription_expired') {
                return 'info';
            }
        },
        paymentAmount() {
            return toAmountDisplay({
                amount: window.PAYMENT_AMOUNT,
                currency: window.USER_CURRENCY,
            });
        },
        fromDate() {
            return window.FROM_DATE;
        },
        isProxy() {
            return window.IS_PROXY;
        },
        usePaymentsIntent() {
            return window.USE_PAYMENTS_INTENT;
        },
        isAcceptJs() {
            return window.USE_ACCEPT_JS;
        },
        isCreditCard() {
            return this.client.payment_method_id == PAYMENT_METHOD_CREDIT_CARD;
        },
        isECheck() {
            return this.client.payment_method_id == PAYMENT_METHOD_E_CHECK;
        },
    },
    mounted() {
        (async () => {
            try {
                this.$store.commit('onlineBooking/gotBusy', true);
                await Promise.all([this.fetchTechReservations(), this.fetchClient()]);
            } finally {
                this.$store.commit('onlineBooking/gotBusy', false);
            }
        })();
        this.gotStripeClientSecret(window.STRIPE_CLIENT_SECRET);
        if (this.isAcceptJs) this.loadAccept(window.ACCEPT_JS_SRC_SCRIPT);
    },
    methods: {
        ...mapActions('accept', ['loadAccept', 'dispatchAcceptData']),
        ...mapMutations('onlineBooking', ['gotClient', 'gotStripeClientSecret']),
        formatDate(value) {
            return value && format(new Date(value * 1000), this.userUnicodeDateFormat);
        },
        async fetchTechReservations() {
            try {
                const { data } = await API.get(
                    ONLINE_BOOKING_PAYMENT_REQUEST_TECH_RESERVATIONS_ENDPOINT(window.PAYMENT_REQUEST_UUID)
                );
                this.techReservations = data;
            } catch (e) {
                this.errorToast({ text: 'Could not load reservation data.' });
            }
        },
        async fetchClient() {
            try {
                const response = await API.get(
                    ONLINE_BOOKING_PAYMENT_REQUEST_CLIENT_ENDPOINT(window.PAYMENT_REQUEST_UUID)
                );
                this.gotClient(response);
            } catch (e) {
                this.errorToast({ text: 'Could not load client data.' });
            }
        },
        getAddress() {
            return {
                line1: this.client.address1,
                line2: this.client.address2 || '',
                city: this.client.city,
                state: this.states.find((s) => s.id == this.client.state_id)?.code || '',
                country: this.countries.find((c) => c.id == this.client.country_id)?.code || '',
                postal_code: this.client.zip,
            };
        },
        formatFieldsErrorMessage(errorObject) {
            if (!errorObject || typeof errorObject !== 'object') return 'Invalid error object';
            const fieldName = Object.keys(errorObject)[0];
            const trimmedFieldName = fieldName.replace(/_/g, ' ').trim();
            const errorMessage = errorObject[fieldName][0];
            return `<b>Field</b>: ${
                trimmedFieldName.charAt(0).toUpperCase() + trimmedFieldName.slice(1)
            }.</br><b>Message</b>: ${errorMessage}`;
        },
        async validateClientFields() {
            this.$store.commit('onlineBooking/gotBusy', true);
            try {
                await API.post(
                    ONLINE_BOOKING_PAYMENT_REQUEST_VALIDATE_CLIENT_FIELDS_ENDPOINT(window.PAYMENT_REQUEST_UUID),
                    this.client
                );
                return true;
            } catch (err) {
                const { generic, fields } = formatResponseError(err);
                if (generic) this.errorToast({ text: generic });
                else if (!isEmpty(fields)) {
                    this.errorModalMessage = this.formatFieldsErrorMessage(fields);
                    this.$bvModal.show(this.paymentErrorModalId);
                } else this.errorToast({ text: 'Something went wrong while validating client fields.' });
                this.$store.commit('onlineBooking/gotBusy', false);
            }
        },
        async submitPayment() {
            this.$store.commit('onlineBooking/gotBusy', true);
            try {
                const valid = await this.validateClientFields();
                if (!valid) return;
                if (this.usePaymentsIntent && this.isCreditCard) {
                    try {
                        const res = await this.stripe.confirmCardPayment(this.stripeClientSecret, {
                            payment_method: {
                                card: this.card,
                                billing_details: {
                                    address: this.getAddress(),
                                    name: this.client.card_name,
                                    email: this.client.email,
                                },
                            },
                        });
                        if (!res.error || res.error.payment_intent.status === 'succeeded') await this.processPayment();
                        else {
                            this.errorModalMessage = res.error.message;
                            this.$bvModal.show(this.paymentErrorModalId);
                        }
                    } catch (err) {
                        this.errorToast({ text: err.message || 'Something went wrong while confirming card.' });
                        return;
                    }
                } else if (this.usePaymentsIntent && this.isECheck) {
                    try {
                        this.stripe
                            .collectBankAccountForPayment({
                                clientSecret: this.stripeClientSecret,
                                params: {
                                    payment_method_type: 'us_bank_account',
                                    payment_method_data: {
                                        billing_details: {
                                            name: this.client.echeck_account_name,
                                            email: this.client.echeck_holder_email,
                                        },
                                    },
                                },
                            })
                            .then(({ paymentIntent, error }) => {
                                if (error) {
                                    this.$store.commit('onlineBooking/gotBusy', false);
                                    this.errorModalMessage = error.message;
                                    this.$bvModal.show(this.paymentErrorModalId);
                                } else if (paymentIntent.status === 'requires_payment_method') {
                                } else if (paymentIntent.status === 'requires_confirmation') {
                                    this.paymentIntent = paymentIntent;
                                    if ('us_bank_account' in paymentIntent.payment_method_options) {
                                        this.showEcheckConfirmation();
                                    }
                                }
                            });
                    } catch (err) {
                        this.errorToast({ text: err });
                        return;
                    }
                } else if (this.isAcceptJs && (!this.isProxy || this.isECheck)) {
                    const secureData = {};
                    secureData.authData = {
                        apiLoginID: window.AUTH_NET_USER_ID,
                        clientKey: window.AUTH_NET_PUBLISHABLE_KEY,
                    };

                    if (this.isCreditCard) {
                        const [month, year] = this.client.card_expiry.split('/');
                        secureData.cardData = {
                            cardNumber: this.client.card_number.replaceAll(' ', ''),
                            month: month,
                            year: year,
                            cardCode: this.client.card_code,
                            fullName: this.client.card_name,
                        };
                    } else {
                        secureData.bankData = {
                            routingNumber: this.client.echeck_routing_no,
                            accountNumber: this.client.echeck_account_no,
                            nameOnAccount: this.client.echeck_account_name,
                            accountType: this.client.echeck_account_type,
                            bankName: this.client.echeck_bank_name,
                        };
                    }

                    const response = await this.dispatchAcceptData(secureData);
                    this.client.data_descriptor = response.opaqueData.dataDescriptor;
                    this.client.data_value = response.opaqueData.dataValue;
                    await this.processPayment();
                } else if (this.isProxy) {
                    await this.processPayment();
                }
            } catch (err) {
                const { generic } = formatResponseError(err);
                this.errorToast({ text: generic || 'Something went wrong while submitting payment.' });
            } finally {
                if (!!this.stripeClientSecret) this.$store.commit('onlineBooking/gotBusy', false);
            }
        },
        showEcheckConfirmation() {
            this.acceptEcheckPaymentDisabled = false;
            this.eCheckAgreementDate = this.formatDate(this.paymentIntent?.created);
            this.eCheckBankName = this.paymentIntent?.payment_method?.us_bank_account?.bank_name;
            this.eCheckRoutingNumber = this.paymentIntent?.payment_method?.us_bank_account?.routing_number;
            this.eCheckAccountNumber = `XXXX${this.paymentIntent?.payment_method?.us_bank_account?.last4}`;
            this.$bvModal.show(this.eCheckConfirmationModalId);
        },
        acceptEcheckPaymentMethod() {
            this.acceptEcheckPaymentDisabled = true;
            this.stripe.confirmUsBankAccountPayment(this.stripeClientSecret, {}).then(async (result) => {
                if (result.error) {
                    this.errorToast({ text: result.error.message });
                } else {
                    if (['requires_action', 'requires_source_action'].includes(result.paymentIntent.status)) {
                        // window.open(
                        //     result.paymentIntent?.next_action?.verify_with_microdeposits?.hosted_verification_url,
                        //     '_blank'
                        // );
                        this.acceptEcheckPaymentDisabled = false;
                        this.showPendingConfirmationModal = true;
                    }
                    this.processPayment();
                }
            });
        },
        processPayment: debounce(async function () {
            const clientCopy = cloneDeep(this.client);
            if (this.isCreditCard && !this.stripeClientSecret) {
                clientCopy.card_code = 'XXX';
                clientCopy.card_number = maskNumber(clientCopy.card_number.replaceAll(' ', '')).slice(-8);
            } else if (this.isECheck && this.isAcceptJs) {
                clientCopy.echeck_routing_no = 'XXXXXXXXX';
                clientCopy.echeck_account_no = maskNumber(clientCopy.echeck_account_no).slice(-8);
                clientCopy.echeck_account_name = 'XXXXXXXXX';
                clientCopy.echeck_bank_name = 'XXXXXXXXX';
            } else if (this.isECheck && !!this.stripeClientSecret) {
                this.acceptEcheckPaymentDisabled = true;
                clientCopy.payment_method_id = PAYMENT_METHOD_E_CHECK;
                clientCopy.payment_intent_id = this.paymentIntent.id;
                clientCopy.echeck_account_name = this.client.echeck_account_name;
            }
            setTimeout(
                async () => {
                    try {
                        this.$store.commit('onlineBooking/gotBusy', true);
                        const { data } = await API.post(
                            ONLINE_BOOKING_PAYMENT_REQUEST_PROCESS_PAYMENT_ENDPOINT(window.PAYMENT_REQUEST_UUID),
                            clientCopy
                        );
                        this.$store.commit('onlineBooking/gotPablowData', data.pablow_data);
                        this.$store.commit(
                            'onlineBooking/gotPaymentMethodConfirmationStatus',
                            data.payment_method_confirmation_status
                        );
                        this.$router.replace({
                            name: 'online-booking-payment-request-confirmation',
                            params: { websiteUrl: this.website.url, requestUUID: window.PAYMENT_REQUEST_UUID },
                        });
                    } catch (err) {
                        const { generic } = formatResponseError(err);
                        if (generic) {
                            this.errorModalMessage = generic;
                            this.$bvModal.show(this.paymentErrorModalId);
                        } else this.errorToast({ text: 'Something went wrong while processing payment.' });
                    } finally {
                        this.$store.commit('onlineBooking/gotBusy', false);
                    }
                },
                this.isECheck && !!this.stripeClientSecret ? 500 : 0
            );
        }, 1000),
    },
};
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

#lx-online-booking-checkout-payment-request .lx-online-booking-checkout-payment-request {
    &__card {
        margin-bottom: 30px;

        &__tech-res {
            margin-bottom: 30px;

            :deep(.card-body) {
                padding: 0px !important;
            }

            &__name {
                font-size: 17px;
                display: flex;
                align-items: center;
                justify-content: space-between;
            }

            &__property {
                @include media-breakpoint-down(md) {
                    flex-direction: column;
                }

                @include media-breakpoint-up(md) {
                    display: grid;
                    grid-template-columns: 1fr 2fr;
                    grid-template-rows: 1fr;
                    grid-row-gap: 0px;
                }

                &__image img {
                    padding: 0.4rem;
                    width: 300px;
                    @media (max-width: 768px) {
                        width: -webkit-fill-available;
                    }
                }

                &__info {
                    padding: 1rem;
                    text-align: center;
                }
            }
        }

        &.client-details {
            :deep(.card-body) {
                padding-bottom: 0.5rem !important;
            }
        }
    }
}
</style>
